import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';

export default function CanonicalAlbumMerge({ sourceAlbumId, impact }) {
  const [mapping, setMapping] = useState(() =>
    Object.fromEntries(
      impact.tracks.map((track) => [
        track.sourceTrackId,
        track.candidateTrackIds.length === 1 ? String(track.candidateTrackIds[0]) : '',
      ]),
    ),
  );
  const [plan, setPlan] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const draft = useAdminDraftGuard(mapping, { enabled: !done, busy });
  async function submit(apply) {
    if (busy || done) return;
    if (
      apply &&
      (!confirmed ||
        !plan?.executionEnabled ||
        plan.blockers.length ||
        !window.confirm(
          '두 리뷰는 보존하고 최근 평가만 집계합니다. 확인한 공식 앨범으로 편입할까요?',
        ))
    )
      return;
    setBusy(true);
    setError('');
    try {
      const response = await api.postAuth(
        `/admin/direct-catalog/albums/${sourceAlbumId}/canonical/${apply ? 'apply' : 'review'}`,
        {
          targetAlbumId: impact.targetAlbumId,
          tracks: impact.tracks
            .filter((track) => mapping[track.sourceTrackId])
            .map((track) => ({
              sourceTrackId: track.sourceTrackId,
              targetTrackId: Number(mapping[track.sourceTrackId]),
            })),
          ...(apply ? { fingerprint: plan.fingerprint, confirmedSameEdition: true } : {}),
        },
      );
      if (apply) {
        draft.markClean();
        setDone(true);
        setPlan(null);
      } else setPlan(response.data.data);
      setConfirmed(false);
    } catch (e) {
      setError(e.message || '다시 사전검사해주세요.');
      setPlan(null);
      setConfirmed(false);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Stack spacing={2}>
      <Typography variant="h6">공식 앨범으로 사용자 기록 편입</Typography>
      <Alert severity="info">
        공식 메타데이터는 유지합니다. 기존 리뷰·댓글·좋아요는 보존하고 이전 리뷰를 열람할 수 있게
        하며, 최근 사용자 평가만 평균에 반영합니다. 아래 대응은 추정 후보이므로 원본과 대조해주세요.
      </Alert>
      {impact.tracks.map((track) => (
        <TextField
          select
          key={track.sourceTrackId}
          label={`${track.title} (#${track.sourceTrackId})의 공식 곡`}
          value={mapping[track.sourceTrackId]}
          disabled={busy || done}
          onChange={(event) => {
            setMapping({ ...mapping, [track.sourceTrackId]: event.target.value });
            setPlan(null);
            setConfirmed(false);
          }}
        >
          <MenuItem value="">대응 미확정</MenuItem>
          {track.candidateTrackIds.map((id) => (
            <MenuItem key={id} value={String(id)}>
              공식 곡 #{id}
            </MenuItem>
          ))}
        </TextField>
      ))}
      <Button disabled={busy || done} onClick={() => submit(false)}>
        기록 편입 사전검사
      </Button>
      {error && <Alert severity="error">{error}</Alert>}
      {done && (
        <Alert severity="success">
          공식 앨범으로 편입했습니다. 기존 리뷰와 이전 기록을 확인해주세요.
        </Alert>
      )}
      {plan && (
        <>
          {plan.blockers.map((message) => (
            <Alert severity="error" key={message}>
              {message}
            </Alert>
          ))}
          {!plan.executionEnabled && (
            <Alert severity="warning">
              통합·복원 DB 검증 전 실행 잠금 상태입니다. 아직 기록을 변경하지 않았습니다.
            </Alert>
          )}
          <FormControlLabel
            label="동일 발매판·각 곡 대응·두 리뷰 보존과 최근 평가 집계를 확인했습니다."
            control={
              <Checkbox
                checked={confirmed}
                disabled={busy || !plan.executionEnabled || Boolean(plan.blockers.length)}
                onChange={(event) => setConfirmed(event.target.checked)}
              />
            }
          />
          <Button
            variant="contained"
            disabled={busy || !confirmed || !plan.executionEnabled || Boolean(plan.blockers.length)}
            onClick={() => submit(true)}
          >
            확인한 기록 편입 실행
          </Button>
        </>
      )}
    </Stack>
  );
}
